import type { ApexOptions } from 'apexcharts'
import type { ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatDate, ISO_DATE } from '@/lib/dateTime'

export interface ChartConfigParams {
  chartSeries: ComputedRef<{ x: string; y: number }[]>
  tooltipText: string
  onSelect?: (payload: string) => void
}

export function createBarChartConfig({
  chartSeries,
  tooltipText,
  onSelect,
}: ChartConfigParams): ApexOptions {
  const { t, locale } = useI18n()
  return {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      events: {
        dataPointSelection(event, chartContext, config) {
          //@ts-expect-error: Todo
          onSelect?.(chartSeries.value[0].data[config.dataPointIndex].x)
        },
      },
    },
    xaxis: {
      labels: {
        show: true,
        rotate: 0,
        formatter: (value, timestamp, opts) => {
          if (!opts) return ''
          const { i: index, w } = opts
          if (index === undefined) return ''
          const [series] = w.config.series

          const dayOfFirstItem = new Date(series.data[0].x).getDay()
          const date = new Date(value)
          const areDaysEqual = dayOfFirstItem === date.getDay()

          if (areDaysEqual)
            return Intl.DateTimeFormat(locale.value, { weekday: 'short' }).format(date)
          return ''
        },
      },
      axisTicks: { show: false },
      axisBorder: { show: true },
    },
    yaxis: {
      show: true,
      axisBorder: { show: true },
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      custom({ w, dataPointIndex }) {
        const tooltip = t(tooltipText)
        const x = w.config.series[0].data[dataPointIndex].x
        const y = w.config.series[0].data[dataPointIndex].y
        const dayShorthand = Intl.DateTimeFormat(locale.value, { weekday: 'short' }).format(
          new Date(x)
        )
        return `${formatDate(x, ISO_DATE) + ` (${dayShorthand})`} - ${tooltip} ${y?.toLocaleString() || 0}`
      },
    },
    legend: {
      show: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '95%',
      },
    },
    stroke: {
      show: false,
    },
  }
}
